import React, { type FC, useCallback, useEffect, useState } from "react";
import { useIncentivizeLoginConfirmationParam } from "clientState/AuthFlow/funnels";
import { useUser } from "clientState/useUser";
import {
  useBrand,
  useTrackElement,
  useTracking,
  useTranslation,
} from "@edreamsodigeo/frontend-core";
import {
  BackDrop,
  Body,
  Box,
  Flex,
  Heading,
  Image,
  Modal,
  ModalCloseIcon,
  ModalContent,
  Pill,
  useFadeToggle,
} from "@edreamsodigeo/prisma-design-system";

import { useTools } from "../../../tools/useTools";
import {
  ACTION,
  CATEGORY,
  DATAMESH_BACKDROP_ONCLICK_EVENT,
  DATAMESH_ONCLICK_EVENT,
  DATAMESH_ONLOAD_EVENT,
  LABEL,
} from "./config/tracking";
import { CLOSE_BUTTON, GREEN_AREA, INSIDE, logoSrcMap } from "./constants";

export const WelcomePopup: FC = () => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  const [isOpen, setIsOpen] = useState(true);

  const modalProps = useFadeToggle({ isOpen });
  const brand = useBrand();
  const { isMobile } = useTools();
  const { user } = useUser();
  const { hasLoginConfirmationParam } = useIncentivizeLoginConfirmationParam();
  const displayName = user?.displayName || "";

  const src = logoSrcMap[brand];

  const trackDisplayRef = useTrackElement(["display"], DATAMESH_ONLOAD_EVENT);
  const trackClickCloseRef = useTrackElement(["click"], DATAMESH_ONCLICK_EVENT);
  const trackClickBackdropRef = useTrackElement(
    ["click"],
    DATAMESH_BACKDROP_ONCLICK_EVENT,
  );

  const onTracking = useCallback(
    (label: string) => {
      trackEvent({
        category: CATEGORY,
        action: ACTION,
        label,
      });
    },
    [trackEvent],
  );

  const onClose = (): void => {
    onTracking(LABEL.CLOSE);

    setIsOpen(false);
  };

  const onClick = (click: string): void => {
    if (click === CLOSE_BUTTON) {
      onClose();
    } else if (click === GREEN_AREA) {
      onTracking(LABEL.BENEFIT);
    }

    onTracking(LABEL.CLOSE);
    if (isOpen) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!hasLoginConfirmationParam) {
      onTracking(LABEL.RENDER);
      onTracking(LABEL.OPEN);
    }
  }, [onTracking, hasLoginConfirmationParam]);

  return !hasLoginConfirmationParam ? (
    <Modal
      {...modalProps}
      onClose={onClose}
      size={isMobile ? "small" : "medium"}
      ref={trackDisplayRef}
      backDrop={
        <BackDrop
          onClick={onClose}
          open={isOpen}
          ref={trackClickBackdropRef}
          data-testid="welcome-popup-backdrop"
        />
      }
    >
      <Box ref={trackClickCloseRef}>
        <ModalCloseIcon
          aria-label="closeIcon"
          onClose={(): void => onClick(CLOSE_BUTTON)}
        />
      </Box>
      <ModalContent onClick={(): void => onClick(INSIDE)}>
        <Flex p="1x_big" justifyContent="center">
          <Image src={src} sx={{ width: "203px" }} alt="logo" />
        </Flex>

        <Flex mb="base" justifyContent="center">
          <Heading
            type={isMobile ? "3" : "2"}
            textAlign="center"
            weight="medium"
            color="neutral.100"
          >
            {t("prime.welcome.popup.title", { displayName })}
          </Heading>
        </Flex>

        <Flex my="base" justifyContent="center">
          <Body
            type="base"
            textAlign="center"
            weight="normal"
            color="neutral.70"
          >
            {t("prime.welcome.popup.description")}
          </Body>
        </Flex>
      </ModalContent>

      <Flex
        onClick={(): void => onClick(GREEN_AREA)}
        width="100%"
        backgroundColor="green.20"
        p="2x_big"
        justifyContent="center"
      >
        <Body type="base" weight="bold" textAlign="center" color="neutral.80">
          {t("prime.welcome.popup.benefit")}
          <Pill sx={{ margin: "0px 8px" }} type="positive" rounded>
            {t("prime.welcome.popup.pill")}
          </Pill>
        </Body>
      </Flex>
    </Modal>
  ) : null;
};
